import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { api, getTenantSlug } from '../config/api';
import {
  FolderIcon,
  FolderPlusIcon,
  VideoCameraIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon,
  HomeIcon,
  CheckIcon,
  CheckCircleIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  LinkIcon,
  BookOpenIcon,
  ChevronDownIcon,
  PlusIcon,
  ShareIcon,
  UserGroupIcon,
  FolderArrowDownIcon,
  SparklesIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PlayIcon as PlaySolid } from '@heroicons/react/24/solid';

const TEACHING_ROLES = ['institution_admin', 'institution_sub_admin', 'teacher'];

// ── Types (mirror backend schemas/video.py) ───────────────────────────────
interface VideoFolder {
  id: string;
  parent_id?: string | null;
  name: string;
  subfolder_count?: number | null;
  video_count?: number | null;
  ancestors?: { id: string; name: string }[] | null;
}
interface Video {
  id: string;
  title: string;
  source_type: 'upload' | 'youtube' | 'gdrive' | 'onedrive' | 'url';
  external_url?: string | null;
  thumbnail_url?: string | null;
  book_id?: string | null;
  book_title?: string | null;
  language?: string | null;
  chapter_number?: number | null;
  concept_tags?: string[] | null;
  status: string;
  is_published: boolean;
  folder_id?: string | null;
  playback_url?: string | null;
}
interface BookOption { id: string; title: string; author: string; cover_url?: string | null; processed?: boolean; }
interface ChapterOption { number: number; name: string; }
interface GroupOption { id: string; name: string; student_count?: number | null; }
// What the Share modal is acting on. A folder share flows to its whole subtree.
type ShareTarget = { kind: 'video' | 'folder'; id: string; name: string };

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
const VIDEO_DND = 'application/x-vidya-video';

// Per-item cover gradients — same set the book Library uses, so a video without
// a thumbnail reads as part of the same product, not a placeholder.
const COVER = [
  'from-cyan-400 to-blue-500', 'from-emerald-400 to-teal-500', 'from-blue-400 to-violet-500',
  'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500', 'from-violet-400 to-fuchsia-500',
];
const coverFor = (id: string) => COVER[id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COVER.length];

const youTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const thumbFor = (v: Video): string | null => {
  if (v.thumbnail_url) return v.thumbnail_url;
  const yt = v.source_type === 'youtube' ? youTubeId(v.external_url) : null;
  return yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : null;
};

const gridV = { hidden: {}, show: { transition: { staggerChildren: 0.03 } } };
const itemV = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
};

// ═══════════════════════════════════════════════════════════════════════════
// Role dispatch: one surface, two experiences. Managers (teachers/admins of this
// tenant) get the Drive-style library; everyone else gets a flat, read-only grid
// of the lectures shared with them. The backend enforces this independently — the
// client check just picks which UI to render.
// ═══════════════════════════════════════════════════════════════════════════
const VideoLibrary: React.FC = () => {
  const { user } = useAuth();
  const tenantSlug = getTenantSlug();
  const isManager =
    !!user?.is_admin ||
    !!user?.institution_memberships?.some(
      (m: any) => m.institution_slug === tenantSlug && TEACHING_ROLES.includes(m.role),
    );
  return isManager ? <ManagerLibrary /> : <StudentLibrary />;
};

// ═══════════════════════════════════════════════════════════════════════════
const ManagerLibrary: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { tenant } = useTenant();
  const institutionId = tenant?.institution_id;
  const navigate = useNavigate();

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [crumbs, setCrumbs] = useState<{ id: string; name: string }[]>([]);
  const [folders, setFolders] = useState<VideoFolder[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  // Saved assignments filed into this folder (the Drive leg of Assignment Studio).
  const [assignments, setAssignments] = useState<{ id: string; title: string; updated_at?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderDraft, setFolderDraft] = useState('');
  const [modal, setModal] = useState<{ kind: 'add' } | { kind: 'edit'; video: Video } | null>(null);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [moveTarget, setMoveTarget] = useState<Video | null>(null);
  const [dragVideoId, setDragVideoId] = useState<string | null>(null);

  const loadLevel = useCallback(async (folderId: string | null) => {
    setLoading(true); setError('');
    try {
      const [f, v, asg] = await Promise.all([
        api.get<VideoFolder[]>('/api/videos/folders', { params: folderId ? { parent_id: folderId } : {} }),
        api.get<Video[]>('/api/videos', { params: folderId ? { folder_id: folderId } : {} }),
        // Saved assignments are always filed in a folder; the Drive root has none.
        folderId ? api.get<any[]>('/api/assignments', { params: { folder_id: folderId } }) : Promise.resolve({ data: [] as any[] }),
      ]);
      setFolders(f.data); setVideos(v.data); setAssignments(asg.data || []);
      if (folderId) {
        const d = await api.get<VideoFolder>(`/api/videos/folders/${folderId}`);
        setCrumbs(d.data.ancestors || []);
      } else setCrumbs([]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load the video library.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLevel(currentFolderId); }, [currentFolderId, loadLevel]);

  const reload = () => loadLevel(currentFolderId);
  const openFolder = (id: string | null) => { setQuery(''); setCurrentFolderId(id); };

  const createFolder = async () => {
    const name = folderDraft.trim();
    if (!name) { setCreatingFolder(false); setFolderDraft(''); return; }
    try { await api.post('/api/videos/folders', { name, parent_id: currentFolderId }); setCreatingFolder(false); setFolderDraft(''); reload(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Failed to create folder.'); }
  };
  const renameFolder = async (id: string, name: string) => {
    try { await api.patch(`/api/videos/folders/${id}`, { name }); reload(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Failed to rename folder.'); }
  };
  const deleteFolder = async (id: string) => {
    if (!window.confirm('Delete this folder? It must be empty.')) return;
    try { await api.delete(`/api/videos/folders/${id}`); reload(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Folder is not empty.'); }
  };
  const deleteVideo = async (id: string) => {
    if (!window.confirm('Remove this video from the library?')) return;
    try { await api.delete(`/api/videos/${id}`); reload(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Failed to delete video.'); }
  };
  const moveVideo = async (videoId: string, folderId: string | null) => {
    try { await api.patch(`/api/videos/${videoId}`, { folder_id: folderId }); reload(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Failed to move video.'); }
  };
  const openVideo = (v: Video) => navigate(`/watch/${v.id}`);

  const q = query.trim().toLowerCase();
  const shownFolders = useMemo(() => folders.filter((f) => !q || f.name.toLowerCase().includes(q)), [folders, q]);
  const shownVideos = useMemo(() => videos.filter((v) =>
    !q || v.title.toLowerCase().includes(q) || (v.book_title || '').toLowerCase().includes(q)
    || (v.concept_tags || []).some((c) => c.toLowerCase().includes(q))), [videos, q]);
  const shownAssignments = useMemo(() => assignments.filter((x) => !q || x.title.toLowerCase().includes(q)), [assignments, q]);
  const empty = !loading && shownFolders.length === 0 && shownVideos.length === 0 && shownAssignments.length === 0;

  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const sub = isDarkMode ? 'text-slate-400' : 'text-gray-500';
  const ghostBtn = isDarkMode
    ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-500/60'
    : 'border-cyan-500/60 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-600';
  const inputWrap = isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-300 bg-white';

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-5 py-7 md:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400">Library</div>
            <h1 className={cx('mt-1 text-2xl font-bold sm:text-3xl', text)}>Content Library</h1>
            <p className={cx('mt-1 text-sm', sub)}>
              {folders.length} folder{folders.length === 1 ? '' : 's'} · {videos.length} video{videos.length === 1 ? '' : 's'} here
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setCreatingFolder(true); setFolderDraft(''); }}
              className={cx('inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all', ghostBtn)}>
              <FolderPlusIcon className="h-5 w-5" /> New folder
            </button>
            <button onClick={() => setModal({ kind: 'add' })}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-glow-cyan transition-transform active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
              <ArrowUpTrayIcon className="h-5 w-5" /> Add video
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Breadcrumbs crumbs={crumbs} currentId={currentFolderId} isDark={isDarkMode}
            onNavigate={openFolder} onDropVideo={(fid) => { if (dragVideoId) moveVideo(dragVideoId, fid); }} />
          <div className="flex items-center gap-2">
            <div className={cx('flex items-center gap-2 rounded-xl border px-3 py-2', inputWrap)}>
              <MagnifyingGlassIcon className={cx('h-4 w-4', sub)} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this folder…"
                className={cx('w-36 bg-transparent text-sm outline-none placeholder:text-slate-400 sm:w-48', text)} />
            </div>
            <div className={cx('flex rounded-xl border p-1', inputWrap)}>
              {([['grid', Squares2X2Icon], ['list', ListBulletIcon]] as const).map(([k, Icon]) => (
                <button key={k} onClick={() => setView(k)} title={`${k} view`}
                  className={cx('rounded-lg p-1.5 transition', view === k ? 'bg-cyan-500 text-white' : sub)}>
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* inline new folder */}
        <AnimatePresence>
          {creatingFolder && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="mt-4 flex items-center gap-2 overflow-hidden">
              <div className={cx('flex items-center gap-2 rounded-xl border px-3 py-2.5', isDarkMode ? 'border-cyan-500/50 bg-slate-800/50' : 'border-cyan-400 bg-white')}>
                <FolderIcon className="h-5 w-5 text-cyan-500" />
                <input autoFocus value={folderDraft} onChange={(e) => setFolderDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setFolderDraft(''); } }}
                  placeholder="Folder name" className={cx('w-44 bg-transparent text-sm outline-none', text)} />
              </div>
              <button onClick={createFolder} className="rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-600">Create</button>
              <button onClick={() => { setCreatingFolder(false); setFolderDraft(''); }} className={cx('px-2 text-sm', sub)}>Cancel</button>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="mt-4 flex items-center justify-between rounded-xl bg-red-500/10 px-4 py-2.5 text-sm text-red-500">
            <span>{error}</span><button onClick={() => setError('')}><XMarkIcon className="h-4 w-4" /></button>
          </div>
        )}

        {/* Body */}
        <div className="mt-7">
          {loading ? (
            <Skeleton isDark={isDarkMode} />
          ) : empty ? (
            <EmptyState isDark={isDarkMode} filtered={!!q} onAdd={() => setModal({ kind: 'add' })} onNewFolder={() => setCreatingFolder(true)} />
          ) : (
            <motion.div variants={gridV} initial="hidden" animate="show">
              {shownFolders.length > 0 && (
                <>
                  <SectionLabel isDark={isDarkMode}>Folders</SectionLabel>
                  <div className={cx('mb-9 grid gap-3', view === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1')}>
                    {shownFolders.map((f) => (
                      <FolderCard key={f.id} folder={f} isDark={isDarkMode} dragging={!!dragVideoId}
                        onOpen={() => openFolder(f.id)} onRename={renameFolder} onDelete={() => deleteFolder(f.id)}
                        onShare={() => setShareTarget({ kind: 'folder', id: f.id, name: f.name })}
                        onDropVideo={(vid) => moveVideo(vid, f.id)} />
                    ))}
                  </div>
                </>
              )}
              {shownAssignments.length > 0 && (
                <>
                  <SectionLabel isDark={isDarkMode}>Assignments</SectionLabel>
                  <div className={cx('mb-9 grid gap-3', view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1')}>
                    {shownAssignments.map((a2) => (
                      <button key={a2.id} type="button" onClick={() => navigate(`/assignments/${a2.id}/view`)}
                        className={cx('flex items-center gap-3 rounded-2xl border p-3.5 text-left transition hover:-translate-y-0.5',
                          isDarkMode ? 'border-slate-700 bg-slate-900/60 hover:border-cyan-500/40' : 'border-gray-200 bg-white hover:border-cyan-400/60 hover:shadow-md')}>
                        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300">
                          <ClipboardDocumentCheckIcon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cx('block truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{a2.title}</span>
                          <span className={cx('block text-[11px] font-medium uppercase tracking-wider', isDarkMode ? 'text-slate-500' : 'text-gray-400')}>Assignment</span>
                        </span>
                        <span role="button" tabIndex={0} title="Edit in Assignment Studio"
                          onClick={(e) => { e.stopPropagation(); navigate(`/assignment-studio/${a2.id}`); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/assignment-studio/${a2.id}`); } }}
                          className={cx('flex h-8 w-8 flex-none items-center justify-center rounded-lg transition',
                            isDarkMode ? 'text-slate-500 hover:bg-slate-800 hover:text-cyan-300' : 'text-gray-400 hover:bg-gray-100 hover:text-cyan-600')}>
                          <PencilSquareIcon className="h-4 w-4" />
                        </span>
                        <ChevronRightIcon className={cx('h-4 w-4 flex-none', isDarkMode ? 'text-slate-600' : 'text-gray-300')} />
                      </button>
                    ))}
                  </div>
                </>
              )}
              {shownVideos.length > 0 && (
                <>
                  <SectionLabel isDark={isDarkMode}>Videos</SectionLabel>
                  <div className={cx('grid gap-5', view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1')}>
                    {shownVideos.map((v) => (
                      <VideoCard key={v.id} video={v} isDark={isDarkMode} list={view === 'list'}
                        onOpen={() => openVideo(v)} onEdit={() => setModal({ kind: 'edit', video: v })} onDelete={() => deleteVideo(v.id)}
                        onShare={() => setShareTarget({ kind: 'video', id: v.id, name: v.title })}
                        onMove={() => setMoveTarget(v)}
                        onDragStart={() => setDragVideoId(v.id)} onDragEnd={() => setDragVideoId(null)} />
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {modal && (
          <UploadModal key="upload" isDark={isDarkMode} folderId={currentFolderId}
            video={modal.kind === 'edit' ? modal.video : undefined}
            onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />
        )}
        {shareTarget && (
          <ShareModal key="share" isDark={isDarkMode} institutionId={institutionId}
            target={shareTarget} onClose={() => setShareTarget(null)} />
        )}
        {moveTarget && (
          <MoveModal key="move" isDark={isDarkMode} video={moveTarget}
            onClose={() => setMoveTarget(null)} onDone={() => { setMoveTarget(null); reload(); }} />
        )}
      </AnimatePresence>
    </Layout>
  );
};

// ── Primitives ───────────────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode; isDark: boolean }> = ({ children, isDark }) => (
  <div className={cx('mb-3 text-[11px] font-semibold uppercase tracking-[0.16em]', isDark ? 'text-slate-500' : 'text-gray-400')}>{children}</div>
);

const cardCls = (isDark: boolean) => isDark
  ? 'bg-slate-900/50 border border-slate-700/50 hover:border-slate-600'
  : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg';

const Skeleton: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const base = isDark ? 'bg-slate-800/60' : 'bg-gray-200/70';
  return (
    <div>
      <div className={cx('mb-3 h-3 w-16 animate-pulse rounded', base)} />
      <div className="mb-9 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className={cx('h-[60px] animate-pulse rounded-2xl', base)} />)}
      </div>
      <div className={cx('mb-3 h-3 w-16 animate-pulse rounded', base)} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={cx('overflow-hidden rounded-2xl border', isDark ? 'border-slate-700/50' : 'border-gray-200')}>
            <div className={cx('aspect-video animate-pulse', base)} />
            <div className="space-y-2 p-4"><div className={cx('h-3.5 w-3/4 animate-pulse rounded', base)} /><div className={cx('h-3 w-1/2 animate-pulse rounded', base)} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Breadcrumbs: React.FC<{
  crumbs: { id: string; name: string }[]; currentId: string | null; isDark: boolean;
  onNavigate: (id: string | null) => void; onDropVideo: (folderId: string | null) => void;
}> = ({ crumbs, currentId, isDark, onNavigate, onDropVideo }) => {
  const [over, setOver] = useState<string | null>(null);
  const sub = isDark ? 'text-slate-400' : 'text-gray-500';
  const crumb = (id: string | null, label: React.ReactNode, isCurrent: boolean) => (
    <button onClick={() => onNavigate(id)}
      onDragOver={(e) => { e.preventDefault(); setOver(id ?? 'root'); }} onDragLeave={() => setOver(null)}
      onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.getData(VIDEO_DND)) onDropVideo(id); setOver(null); }}
      className={cx('inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm transition',
        over === (id ?? 'root') ? 'bg-cyan-500/15 ring-1 ring-cyan-500/40' : 'hover:bg-slate-500/10',
        isCurrent ? cx('font-semibold', isDark ? 'text-white' : 'text-gray-900') : sub)}>
      {label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-0.5">
      {crumb(null, <span className="inline-flex items-center gap-1"><HomeIcon className="h-4 w-4" /> Library</span>, currentId === null)}
      {crumbs.map((c, i) => (
        <span key={c.id} className="inline-flex items-center gap-0.5">
          <ChevronRightIcon className={cx('h-3.5 w-3.5', sub)} />{crumb(c.id, c.name, i === crumbs.length - 1)}
        </span>
      ))}
    </div>
  );
};

// ── Folder card ─────────────────────────────────────────────────────────
const FolderCard: React.FC<{
  folder: VideoFolder; isDark: boolean; dragging: boolean;
  onOpen: () => void; onRename: (id: string, name: string) => void; onDelete: () => void; onShare: () => void; onDropVideo: (videoId: string) => void;
}> = ({ folder, isDark, dragging, onOpen, onRename, onDelete, onShare, onDropVideo }) => {
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const text = isDark ? 'text-white' : 'text-gray-900';
  const sub = isDark ? 'text-slate-400' : 'text-gray-500';
  return (
    <motion.div variants={itemV} layout onClick={() => !editing && onOpen()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData(VIDEO_DND); if (id) onDropVideo(id); setOver(false); }}
      className={cx('group relative flex cursor-pointer items-center gap-3 rounded-2xl p-3.5 transition-all',
        over ? 'border border-cyan-500 ring-2 ring-cyan-500/30' : cardCls(isDark),
        dragging && !over && 'border-dashed')}>
      <span className={cx('flex h-10 w-10 flex-none items-center justify-center rounded-xl transition',
        over ? 'bg-cyan-500 text-white' : 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-300')}>
        <FolderIcon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        {editing ? (
          <input autoFocus value={name} onClick={(e) => e.stopPropagation()} onChange={(e) => setName(e.target.value)}
            onBlur={() => { setEditing(false); if (name.trim() && name !== folder.name) onRename(folder.id, name.trim()); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setName(folder.name); setEditing(false); } }}
            className={cx('w-full rounded border bg-transparent px-1 text-sm font-semibold outline-none', isDark ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-900')} />
        ) : <p className={cx('truncate text-sm font-semibold', text)}>{folder.name}</p>}
        <p className={cx('text-xs', sub)}>{folder.subfolder_count || 0} folders · {folder.video_count || 0} videos</p>
      </div>
      <div className="flex flex-none items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
        <button onClick={(e) => { e.stopPropagation(); onShare(); }} title="Share with classes" className={cx('rounded-md p-1.5 hover:bg-cyan-500/10 hover:text-cyan-500', sub)}><ShareIcon className="h-4 w-4" /></button>
        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Rename" className={cx('rounded-md p-1.5 hover:bg-slate-500/10', sub)}><PencilIcon className="h-4 w-4" /></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete" className={cx('rounded-md p-1.5 hover:bg-red-500/10 hover:text-red-500', sub)}><TrashIcon className="h-4 w-4" /></button>
      </div>
    </motion.div>
  );
};

// ── Video card ─────────────────────────────────────────────────────────
// Processing-state badge on the thumbnail (bottom-right, clear of the hover action buttons).
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const base = 'absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold backdrop-blur-sm';
  if (status === 'ready')
    return <span className={cx(base, 'bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-400/40')}><SparklesIcon className="h-3 w-3" />Notes</span>;
  if (status === 'processing')
    return <span className={cx(base, 'bg-cyan-500/90 text-white')}><span className="h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-white border-t-transparent" />Indexing</span>;
  if (status === 'failed')
    return <span className={cx(base, 'bg-red-500/90 text-white')}><ExclamationTriangleIcon className="h-3 w-3" />Failed</span>;
  return <span className={cx(base, 'bg-black/55 text-slate-200')}><ClockIcon className="h-3 w-3" />Not processed</span>;
};

const VideoCard: React.FC<{
  video: Video; isDark: boolean; list: boolean; readOnly?: boolean;
  onOpen: () => void; onEdit?: () => void; onDelete?: () => void; onShare?: () => void; onMove?: () => void; onDragStart?: () => void; onDragEnd?: () => void;
}> = ({ video, isDark, list, readOnly, onOpen, onEdit, onDelete, onShare, onMove, onDragStart, onDragEnd }) => {
  const text = isDark ? 'text-slate-200' : 'text-gray-800';
  const sub = isDark ? 'text-slate-400' : 'text-gray-500';
  const thumb = thumbFor(video);
  const isYT = video.source_type === 'youtube';

  const poster = (
    <div className={cx('relative overflow-hidden', list ? 'h-[68px] w-[120px] flex-none rounded-xl' : 'aspect-video w-full')}>
      {thumb
        ? <img src={thumb} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" loading="lazy" />
        : <div className={cx('flex h-full w-full items-center justify-center bg-gradient-to-br', coverFor(video.id))}>
            <VideoCameraIcon className="absolute h-20 w-20 text-white/15" />
            <PlaySolid className="relative h-7 w-7 text-white/90" />
          </div>}
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/25 group-hover:opacity-100">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 shadow-lg"><PlaySolid className="ml-0.5 h-5 w-5 text-gray-900" /></span>
      </div>
      <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
        {isYT ? <LinkIcon className="h-3 w-3" /> : <VideoCameraIcon className="h-3 w-3" />}{isYT ? 'YouTube' : 'Upload'}
      </span>
      <StatusBadge status={video.status} />
    </div>
  );

  const body = (
    <div className={cx('min-w-0 flex-1', list ? '' : 'p-4')}>
      <p className={cx('truncate text-sm font-semibold', text)}>{video.title}</p>
      <div className="mt-1.5">
        {video.book_title ? (
          <span className={cx('inline-flex max-w-full items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold ring-1',
            isDark ? 'bg-cyan-500/10 text-cyan-300 ring-cyan-500/25' : 'bg-cyan-50 text-cyan-700 ring-cyan-500/20')}>
            <BookOpenIcon className="h-3.5 w-3.5 flex-none" />
            <span className="truncate">{video.book_title}</span>
            {video.chapter_number ? <span className="flex-none font-medium opacity-70">· Ch {video.chapter_number}</span> : null}
          </span>
        ) : (
          <span className={cx('inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium',
            isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500')}>
            <BookOpenIcon className="h-3.5 w-3.5 flex-none opacity-60" /> Untagged
          </span>
        )}
      </div>
      {!list && video.concept_tags && video.concept_tags.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1">
          {video.concept_tags.slice(0, 3).map((c) => (
            <span key={c} className={cx('truncate rounded-full px-2 py-0.5 text-[10px] font-medium', isDark ? 'bg-slate-800 text-slate-300' : 'bg-gray-100 text-gray-600')}>{c}</span>
          ))}
          {video.concept_tags.length > 3 && <span className={cx('text-[10px]', sub)}>+{video.concept_tags.length - 3}</span>}
        </div>
      )}
    </div>
  );

  return (
    <motion.div variants={itemV} layout draggable={!readOnly}
      onDragStart={readOnly ? undefined : (e) => { (e as unknown as React.DragEvent).dataTransfer?.setData(VIDEO_DND, video.id); onDragStart?.(); }} onDragEnd={readOnly ? undefined : onDragEnd}
      onClick={onOpen}
      className={cx('group relative cursor-pointer overflow-hidden rounded-2xl transition-all', cardCls(isDark), list ? 'flex items-center gap-3.5 p-3' : '')}>
      {poster}
      {body}
      {!readOnly && (
        <div className="absolute right-2.5 top-2.5 flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
          <button onClick={(e) => { e.stopPropagation(); onShare?.(); }} title="Share with classes" className="rounded-md bg-black/40 p-1.5 text-white backdrop-blur-sm hover:bg-cyan-500"><ShareIcon className="h-4 w-4" /></button>
          <button onClick={(e) => { e.stopPropagation(); onMove?.(); }} title="Move to folder" className="rounded-md bg-black/40 p-1.5 text-white backdrop-blur-sm hover:bg-cyan-500"><FolderArrowDownIcon className="h-4 w-4" /></button>
          <button onClick={(e) => { e.stopPropagation(); onEdit?.(); }} title="Edit tags" className="rounded-md bg-black/40 p-1.5 text-white backdrop-blur-sm hover:bg-cyan-500"><PencilIcon className="h-4 w-4" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete?.(); }} title="Delete" className="rounded-md bg-black/40 p-1.5 text-white backdrop-blur-sm hover:bg-red-500"><TrashIcon className="h-4 w-4" /></button>
        </div>
      )}
    </motion.div>
  );
};

// ── Empty state ─────────────────────────────────────────────────────────
const EmptyState: React.FC<{ isDark: boolean; filtered: boolean; onAdd: () => void; onNewFolder: () => void }> = ({ isDark, filtered, onAdd, onNewFolder }) => (
  <div className={cx('flex flex-col items-center justify-center rounded-2xl border border-dashed py-20 text-center', isDark ? 'border-slate-700' : 'border-gray-200')}>
    <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-glow-cyan" style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
      <VideoCameraIcon className="h-8 w-8" />
    </span>
    <h3 className={cx('text-lg font-bold', isDark ? 'text-white' : 'text-gray-900')}>{filtered ? 'No matches' : 'Build your content library'}</h3>
    <p className={cx('mt-1 max-w-sm text-sm', isDark ? 'text-slate-400' : 'text-gray-500')}>
      {filtered ? 'Try a different search.' : 'Add lectures and file assignments into folders — everything you share with students lives here.'}
    </p>
    {!filtered && (
      <div className="mt-6 flex items-center gap-2">
        <button onClick={onNewFolder} className={cx('inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold', isDark ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10' : 'border-cyan-500/60 text-cyan-700 hover:bg-cyan-50')}>
          <FolderPlusIcon className="h-5 w-5" /> New folder
        </button>
        <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-glow-cyan" style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
          <ArrowUpTrayIcon className="h-5 w-5" /> Add your first lecture
        </button>
      </div>
    )}
  </div>
);

// ── Book picker (cover thumbnails + search, collapses to a chip) ──────────
const BookCoverThumb: React.FC<{ book: BookOption; size?: 'sm' | 'md' }> = ({ book, size = 'sm' }) => {
  const cls = size === 'md' ? 'h-14 w-10' : 'h-11 w-8';
  return book.cover_url
    ? <img src={book.cover_url} alt="" className={cx('flex-none rounded object-cover shadow-sm', cls)} />
    : <div className={cx('flex flex-none items-center justify-center rounded bg-gradient-to-br text-white/80 shadow-sm', coverFor(book.id), cls)}><BookOpenIcon className="h-4 w-4" /></div>;
};

const BookPicker: React.FC<{ books: BookOption[]; value: string; err?: string; isDark: boolean; onChange: (id: string) => void }> = ({ books, value, err, isDark, onChange }) => {
  const [q, setQ] = useState('');
  const selected = books.find((b) => b.id === value);
  if (selected) {
    return (
      <div className={cx('flex items-center gap-3 rounded-xl border p-2.5', isDark ? 'border-cyan-500/40 bg-cyan-500/5' : 'border-cyan-500/50 bg-cyan-50')}>
        <BookCoverThumb book={selected} size="md" />
        <div className="min-w-0 flex-1">
          <p className={cx('truncate text-sm font-semibold', isDark ? 'text-white' : 'text-gray-900')}>{selected.title}</p>
          <p className="truncate text-xs text-slate-400">{selected.author}</p>
        </div>
        <button type="button" onClick={() => onChange('')} className="flex-none rounded-lg px-2.5 py-1 text-xs font-semibold text-cyan-500 hover:bg-cyan-500/10">Change</button>
      </div>
    );
  }
  const filtered = books.filter((b) => !q || `${b.title} ${b.author}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className={cx('overflow-hidden rounded-xl border', isDark ? 'border-slate-700' : 'border-gray-200')}>
      <div className={cx('flex items-center gap-2 border-b px-3 py-2', isDark ? 'border-slate-700 bg-slate-800/40' : 'border-gray-200 bg-gray-50')}>
        <MagnifyingGlassIcon className="h-4 w-4 flex-none text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search books to tag…"
          className={cx('w-full bg-transparent text-sm outline-none placeholder:text-slate-400', isDark ? 'text-white' : 'text-gray-900')} />
      </div>
      <div className="max-h-48 space-y-0.5 overflow-y-auto p-1.5">
        {err ? <p className="px-2 py-3 text-xs text-red-400">{err}</p>
          : filtered.length === 0 ? <p className="px-2 py-3 text-xs text-slate-400">No books found.</p>
          : filtered.map((b) => (
            <button type="button" key={b.id} onClick={() => onChange(b.id)}
              className={cx('flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left transition', isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-50')}>
              <BookCoverThumb book={b} />
              <div className="min-w-0 flex-1">
                <p className={cx('truncate text-sm font-medium', isDark ? 'text-slate-200' : 'text-gray-800')}>{b.title}</p>
                <p className="truncate text-xs text-slate-400">{b.author}</p>
              </div>
              {b.processed === false && (
                <span className="flex-none rounded bg-amber-warm-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-warm-600 dark:text-amber-warm-400">unprocessed</span>
              )}
            </button>
          ))}
      </div>
    </div>
  );
};

// ── Single-select dropdown (chapter) — in-flow accordion, no clipping ─────
const SingleSelect: React.FC<{ value: string; options: { value: string; label: string }[]; placeholder: string; isDark: boolean; onChange: (v: string) => void }> = ({ value, options, placeholder, isDark, onChange }) => {
  const [open, setOpen] = useState(false);
  const sel = options.find((o) => o.value === value);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={cx('flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition',
          isDark ? 'border-slate-700 bg-slate-800 text-white hover:border-slate-600' : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400',
          open && 'border-cyan-500 ring-2 ring-cyan-500/30')}>
        <span className={cx(!sel && 'text-slate-400')}>{sel ? sel.label : placeholder}</span>
        <ChevronDownIcon className={cx('h-4 w-4 flex-none text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className={cx('mt-1.5 max-h-44 space-y-0.5 overflow-y-auto rounded-lg border p-1', isDark ? 'border-slate-700 bg-slate-800/60' : 'border-gray-200 bg-white')}>
              {options.map((o) => (
                <button type="button" key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                  className={cx('flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm transition',
                    o.value === value ? 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-300' : (isDark ? 'text-slate-200 hover:bg-slate-700/60' : 'text-gray-700 hover:bg-gray-50'))}>
                  {o.label}{o.value === value && <CheckIcon className="h-4 w-4" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Concept multi-select — selected tokens + search + add-list ────────────
const ConceptPicker: React.FC<{ options: string[]; selected: Set<string>; isDark: boolean; onToggle: (c: string) => void }> = ({ options, selected, isDark, onToggle }) => {
  const [q, setQ] = useState('');
  const sel = Array.from(selected);
  const avail = options.filter((o) => !selected.has(o) && (!q || o.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className={cx('overflow-hidden rounded-xl border', isDark ? 'border-slate-700' : 'border-gray-200')}>
      {sel.length > 0 && (
        <div className={cx('flex flex-wrap gap-1.5 border-b p-2.5', isDark ? 'border-slate-700' : 'border-gray-200')}>
          {sel.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full bg-cyan-500/15 px-2.5 py-1 text-xs font-medium text-cyan-600 dark:text-cyan-300">
              {c}<button type="button" onClick={() => onToggle(c)} className="hover:opacity-70"><XMarkIcon className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className={cx('flex items-center gap-2 border-b px-3 py-2', isDark ? 'border-slate-700 bg-slate-800/40' : 'border-gray-200 bg-gray-50')}>
        <MagnifyingGlassIcon className="h-4 w-4 flex-none text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search concepts…"
          className={cx('w-full bg-transparent text-sm outline-none placeholder:text-slate-400', isDark ? 'text-white' : 'text-gray-900')} />
      </div>
      <div className="max-h-36 overflow-y-auto p-2">
        {avail.length === 0 ? (
          <p className="px-1 py-1.5 text-xs text-slate-400">{q ? 'No matches' : 'All concepts added.'}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {avail.map((o) => (
              <button type="button" key={o} onClick={() => onToggle(o)}
                className={cx('inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition active:scale-95',
                  isDark ? 'border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300' : 'border-gray-200 text-gray-600 hover:border-cyan-500/50 hover:text-cyan-600')}>
                <PlusIcon className="h-3 w-3" />{o}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Upload / edit modal ──────────────────────────────────────────────────
const UploadModal: React.FC<{ isDark: boolean; folderId: string | null; video?: Video; onClose: () => void; onDone: () => void }> = ({ isDark, folderId, video, onClose, onDone }) => {
  const editing = !!video;
  const { tenant } = useTenant();
  const institutionId = tenant?.institution_id;
  const [mode, setMode] = useState<'upload' | 'youtube'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragOverFile, setDragOverFile] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState(video?.external_url || '');
  const [title, setTitle] = useState(video?.title || '');
  const [books, setBooks] = useState<BookOption[]>([]);
  const [bookId, setBookId] = useState(video?.book_id || '');
  const [language, setLanguage] = useState(video?.language || '');
  const [chapters, setChapters] = useState<ChapterOption[]>([]);
  const [chapterNumber, setChapterNumber] = useState(video?.chapter_number ? String(video.chapter_number) : '');
  const [conceptOptions, setConceptOptions] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(video?.concept_tags || []));
  const [bookErr, setBookErr] = useState('');
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!institutionId) { setBookErr('no institute context (tenant not resolved)'); return; }
    setBookErr('');
    api.get<any[]>(`/api/institutions/${institutionId}/books`)
      .then((r) => setBooks((r.data || []).map((b) => ({ id: b.id, title: b.title, author: b.author, cover_url: b.cover_url, processed: !!b.processed }))))
      .catch((e) => setBookErr(e?.response?.status ? `books HTTP ${e.response.status}` : 'books request failed'));
  }, [institutionId]);
  useEffect(() => {
    if (!bookId) { setChapters([]); setConceptOptions([]); return; }
    api.get<any>(`/api/books/${bookId}/chapters`).then((r) => {
      const ch = r.data?.chapters || {};
      setChapters(Object.entries(ch).map(([num, m]: [string, any]) => ({ number: Number(num), name: m?.name || `Chapter ${num}` })));
    }).catch(() => setChapters([]));
  }, [bookId]);
  useEffect(() => {
    if (!bookId) { setConceptOptions([]); return; }
    api.get<any>('/api/videos/concept-suggestions', { params: { book_id: bookId, ...(chapterNumber ? { chapter_number: chapterNumber } : {}) } })
      .then((r) => setConceptOptions(r.data?.concepts || [])).catch(() => setConceptOptions([]));
  }, [bookId, chapterNumber]);

  const toggle = (c: string) => setSelected((p) => { const n = new Set(p); n.has(c) ? n.delete(c) : n.add(c); return n; });

  const submit = async () => {
    setErr('');
    if (!title.trim()) return setErr('A title is required.');
    try {
      setSaving(true);
      if (editing) {
        await api.patch(`/api/videos/${video!.id}`, { title: title.trim(), language: language || null, book_id: bookId || null, chapter_number: chapterNumber ? Number(chapterNumber) : null, concept_tags: Array.from(selected) });
      } else {
        if (mode === 'upload' && !file) { setSaving(false); return setErr('Choose a video file.'); }
        if (mode === 'youtube' && !youtubeUrl.trim()) { setSaving(false); return setErr('Paste a YouTube link.'); }
        const fd = new FormData();
        fd.append('title', title.trim());
        if (folderId) fd.append('folder_id', folderId);
        if (language) fd.append('language', language);
        if (bookId) fd.append('book_id', bookId);
        if (chapterNumber) fd.append('chapter_number', chapterNumber);
        if (selected.size) fd.append('concept_tags', JSON.stringify(Array.from(selected)));
        if (mode === 'upload' && file) fd.append('video_file', file);
        if (mode === 'youtube') fd.append('youtube_url', youtubeUrl.trim());
        await api.post('/api/videos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); } });
      }
      onDone();
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Something went wrong.'); setSaving(false); }
  };

  const field = cx('w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30',
    isDark ? 'border-slate-700 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-900');
  const label = cx('mb-1.5 block text-xs font-semibold', isDark ? 'text-slate-300' : 'text-gray-600');

  const twoCol = !editing;
  const sectionHdr = cx('text-[11px] font-semibold uppercase tracking-[0.16em]', isDark ? 'text-slate-500' : 'text-gray-400');
  return (
    <>
      <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.96, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={cx('flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl shadow-lq-glass',
            twoCol ? 'max-w-3xl' : 'max-w-lg',
            isDark ? 'border border-slate-700/60 bg-slate-900' : 'border border-gray-200 bg-white')}>

          {/* Header */}
          <div className={cx('flex flex-none items-start justify-between gap-3 border-b px-7 py-5', isDark ? 'border-slate-800' : 'border-gray-100')}>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">{editing ? 'Edit' : 'Video library'}</div>
              <h2 className={cx('mt-1 text-xl font-bold', isDark ? 'text-white' : 'text-gray-900')}>{editing ? 'Edit video' : 'Add a video'}</h2>
              <p className={cx('mt-0.5 text-sm', isDark ? 'text-slate-400' : 'text-gray-500')}>
                {editing ? 'Update its details and tags.' : 'Upload a lecture or paste a YouTube link, then tag it so it powers chat.'}
              </p>
            </div>
            <button onClick={onClose} className={cx('flex-none rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-500/10', isDark ? 'hover:text-white' : 'hover:text-gray-700')}><XMarkIcon className="h-5 w-5" /></button>
          </div>

          {/* Body */}
          <div className={cx('flex-1 overflow-y-auto px-7 py-6', twoCol && 'md:grid md:grid-cols-2')}>
            {/* Left — the video */}
            {!editing && (
              <div className="space-y-4 md:pr-8">
                <div className={sectionHdr}>The video</div>
                <div className={cx('inline-flex rounded-xl border p-1', isDark ? 'border-slate-700' : 'border-gray-200')}>
                  {(['upload', 'youtube'] as const).map((m) => (
                    <button key={m} onClick={() => setMode(m)} className={cx('rounded-lg px-3.5 py-1.5 text-sm font-medium transition', mode === m ? 'bg-cyan-500 text-white shadow-sm' : (isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'))}>
                      {m === 'youtube' ? 'YouTube link' : 'Upload file'}
                    </button>
                  ))}
                </div>
                {mode === 'upload' ? (
                  file ? (
                    <div className={cx('flex items-center gap-3 rounded-xl border p-3', isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50')}>
                      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-500"><VideoCameraIcon className="h-5 w-5" /></span>
                      <div className="min-w-0 flex-1"><p className={cx('truncate text-sm font-medium', isDark ? 'text-white' : 'text-gray-900')}>{file.name}</p><p className="text-xs text-slate-400">{(file.size / 1e6).toFixed(1)} MB</p></div>
                      <button onClick={() => setFile(null)} className="flex-none rounded-lg p-1 text-slate-400 hover:text-red-500"><XMarkIcon className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <label onDragOver={(e) => { e.preventDefault(); setDragOverFile(true); }} onDragLeave={() => setDragOverFile(false)}
                      onDrop={(e) => { e.preventDefault(); setDragOverFile(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
                      className={cx('flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed px-4 py-10 text-center transition',
                        dragOverFile ? 'border-cyan-500 bg-cyan-500/5' : (isDark ? 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/40' : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50'))}>
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-500/10 text-cyan-500"><ArrowUpTrayIcon className="h-6 w-6" /></span>
                      <span className={cx('text-sm font-semibold', isDark ? 'text-slate-200' : 'text-gray-700')}>Drop a video or click to browse</span>
                      <span className={cx('text-xs', isDark ? 'text-slate-500' : 'text-gray-400')}>MP4, MOV, WebM</span>
                      <input type="file" accept="video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </label>
                  )
                ) : (
                  <div>
                    <label className={label}>YouTube URL</label>
                    <input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…" className={field} />
                    {youTubeId(youtubeUrl) && <img src={`https://img.youtube.com/vi/${youTubeId(youtubeUrl)}/hqdefault.jpg`} alt="" className="mt-3 aspect-video w-full rounded-xl object-cover" />}
                  </div>
                )}
                <div><label className={label}>Title</label><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lecture 12 — Conservation of momentum" className={field} /></div>
              </div>
            )}

            {/* Right — tag it for chat */}
            <div className={cx('space-y-4', twoCol ? cx('mt-6 border-t pt-6 md:mt-0 md:border-l md:border-t-0 md:pl-8 md:pt-0', isDark ? 'border-slate-800' : 'border-gray-100') : '')}>
              <div className={sectionHdr}>{editing ? 'Details' : 'Tag it for chat'}</div>
              {editing && <div><label className={label}>Title</label><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lecture title" className={field} /></div>}
              <div>
                <label className={label}>Spoken language</label>
                <SingleSelect value={language} placeholder="Auto-detect" isDark={isDark}
                  options={[
                    { value: 'english', label: 'English' },
                    { value: 'hindi', label: 'Hindi' },
                    { value: 'gujarati', label: 'Gujarati' },
                    { value: 'marathi', label: 'Marathi' },
                  ]}
                  onChange={(v) => setLanguage(v)} />
                <p className="mt-1.5 text-xs text-slate-400">Language spoken in the lecture — drives transcription. Leave on Auto-detect if unsure.</p>
              </div>
              <div>
                <label className={label}>Primary book</label>
                <BookPicker books={books} value={bookId} err={bookErr} isDark={isDark} onChange={(id) => { setBookId(id); setChapterNumber(''); }} />
                <p className="mt-1.5 text-xs text-slate-400">Links this lecture to a book so chat can cite it.</p>
              </div>
              <AnimatePresence initial={false}>
                {bookId && chapters.length > 0 && (
                  <motion.div key="chapter" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <label className={label}>Chapter</label>
                    <SingleSelect value={chapterNumber} placeholder="Select a chapter" isDark={isDark} options={chapters.map((c) => ({ value: String(c.number), label: c.name }))} onChange={(v) => setChapterNumber(v)} />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence initial={false}>
                {bookId && (chapterNumber || selected.size > 0) && (
                  <motion.div key="concepts" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <label className={label}>Concepts covered</label>
                    {conceptOptions.length > 0 || selected.size > 0
                      ? <ConceptPicker options={conceptOptions} selected={selected} isDark={isDark} onToggle={toggle} />
                      : <p className={cx('rounded-lg border border-dashed px-3 py-3 text-xs', isDark ? 'border-slate-700 text-slate-500' : 'border-gray-200 text-gray-400')}>No concepts found for this chapter.</p>}
                  </motion.div>
                )}
              </AnimatePresence>
              {!bookId && (
                <p className={cx('rounded-xl border border-dashed px-4 py-6 text-center text-xs', isDark ? 'border-slate-800 text-slate-500' : 'border-gray-200 text-gray-400')}>
                  Pick a book to tag the chapter & concepts this video teaches.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={cx('flex-none border-t px-7 py-4', isDark ? 'border-slate-800' : 'border-gray-100')}>
            {err && <div className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</div>}
            {saving && !editing && progress > 0 && (
              <div className="mb-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg,#06B6D4,#3B82F6)' }} animate={{ width: `${progress}%` }} />
                </div>
                <span className="w-9 text-right text-xs font-medium text-slate-400">{progress}%</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose} className={cx('rounded-xl px-4 py-2.5 text-sm font-semibold transition', isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>Cancel</button>
              <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-glow-cyan transition active:scale-[0.98] disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
                {saving ? (editing ? 'Saving…' : 'Adding…') : (editing ? 'Save changes' : 'Add to library')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

// ── Share modal — assign a folder/video to batches (classes) ──────────────
// A share-sheet: each class toggles on/off and persists immediately (POST/DELETE),
// mirroring how Drive's share dialog works. Folder shares cascade to the subtree.
const ShareModal: React.FC<{
  isDark: boolean; institutionId?: string; target: ShareTarget; onClose: () => void;
}> = ({ isDark, institutionId, target, onClose }) => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [shared, setShared] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const base = target.kind === 'folder' ? `/api/videos/folders/${target.id}` : `/api/videos/${target.id}`;

  useEffect(() => {
    if (!institutionId) { setErr('No institute context.'); setLoading(false); return; }
    setLoading(true); setErr('');
    Promise.all([
      api.get<GroupOption[]>(`/api/institutions/${institutionId}/groups`),
      api.get<{ group_id: string }[]>(`${base}/shares`),
    ]).then(([g, s]) => {
      setGroups(g.data || []);
      setShared(new Set((s.data || []).map((x) => x.group_id)));
    }).catch((e: any) => setErr(e?.response?.data?.detail || 'Could not load classes.'))
      .finally(() => setLoading(false));
  }, [institutionId, base]);

  const toggle = async (groupId: string) => {
    if (busy) return;
    setBusy(groupId); setErr('');
    const isShared = shared.has(groupId);
    try {
      if (isShared) {
        await api.delete(`${base}/share/group/${groupId}`);
        setShared((p) => { const n = new Set(p); n.delete(groupId); return n; });
      } else {
        await api.post(`${base}/share/group`, { group_id: groupId });
        setShared((p) => { const n = new Set(p); n.add(groupId); return n; });
      }
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Could not update sharing.'); }
    finally { setBusy(null); }
  };

  const text = isDark ? 'text-white' : 'text-gray-900';
  const sub = isDark ? 'text-slate-400' : 'text-gray-500';

  return (
    <>
      <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.96, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={cx('flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl shadow-lq-glass',
            isDark ? 'border border-slate-700/60 bg-slate-900' : 'border border-gray-200 bg-white')}>

          {/* Header */}
          <div className={cx('flex flex-none items-start justify-between gap-3 border-b px-6 py-5', isDark ? 'border-slate-800' : 'border-gray-100')}>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Share {target.kind}</div>
              <h2 className={cx('mt-1 flex items-center gap-2 text-lg font-bold', text)}>
                {target.kind === 'folder' ? <FolderIcon className="h-5 w-5 flex-none text-cyan-500" /> : <VideoCameraIcon className="h-5 w-5 flex-none text-cyan-500" />}
                <span className="truncate">{target.name}</span>
              </h2>
              <p className={cx('mt-1 text-sm', sub)}>
                {target.kind === 'folder'
                  ? 'Classes you pick can watch every video in this folder and its subfolders.'
                  : 'Pick the classes that can watch this lecture.'}
              </p>
            </div>
            <button onClick={onClose} className={cx('flex-none rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-500/10', isDark ? 'hover:text-white' : 'hover:text-gray-700')}><XMarkIcon className="h-5 w-5" /></button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <div key={i} className={cx('h-14 animate-pulse rounded-xl', isDark ? 'bg-slate-800/60' : 'bg-gray-100')} />)}
              </div>
            ) : groups.length === 0 ? (
              <div className={cx('flex flex-col items-center gap-2 py-10 text-center text-sm', sub)}>
                <UserGroupIcon className="h-8 w-8 text-slate-400" />
                No classes in this institution yet.
              </div>
            ) : (
              <div className="space-y-2">
                {groups.map((g) => {
                  const on = shared.has(g.id);
                  return (
                    <button key={g.id} onClick={() => toggle(g.id)} disabled={busy === g.id}
                      className={cx('flex w-full items-center gap-3 rounded-xl border p-3 text-left transition disabled:opacity-60',
                        on ? 'border-cyan-500/50 bg-cyan-500/5' : isDark ? 'border-slate-700 hover:border-cyan-500/40' : 'border-gray-200 hover:border-cyan-500/40')}>
                      <span className={cx('flex h-9 w-9 flex-none items-center justify-center rounded-lg', on ? 'bg-cyan-500 text-white' : 'bg-blue-500/10 text-blue-600 dark:text-blue-300')}>
                        <UserGroupIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cx('block truncate font-medium', text)}>{g.name}</span>
                        {typeof g.student_count === 'number' && <span className={cx('block text-xs', sub)}>{g.student_count} student{g.student_count === 1 ? '' : 's'}</span>}
                      </span>
                      {busy === g.id
                        ? <span className="h-5 w-5 flex-none animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
                        : on ? <CheckCircleIcon className="h-5 w-5 flex-none text-cyan-500" />
                        : <span className={cx('h-5 w-5 flex-none rounded-full border', isDark ? 'border-slate-600' : 'border-gray-300')} />}
                    </button>
                  );
                })}
              </div>
            )}
            {err && <div className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</div>}
          </div>

          {/* Footer */}
          <div className={cx('flex flex-none items-center justify-between gap-3 border-t px-6 py-4', isDark ? 'border-slate-800' : 'border-gray-100')}>
            <span className={cx('text-xs', sub)}>{shared.size} class{shared.size === 1 ? '' : 'es'} shared</span>
            <button onClick={onClose} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-600">Done</button>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

// ── Move modal — relocate a video into any folder (or back to root) ────────
// Drag-and-drop already moves onto visible folders/breadcrumbs; this picker covers
// moving into any folder in the tree (and back to root), regardless of what's on screen.
const MoveModal: React.FC<{
  isDark: boolean; video: Video; onClose: () => void; onDone: () => void;
}> = ({ isDark, video, onClose, onDone }) => {
  const [folders, setFolders] = useState<VideoFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true); setErr('');
    api.get<VideoFolder[]>('/api/videos/folders', { params: { flat: true } })
      .then((r) => setFolders(r.data || []))
      .catch((e: any) => setErr(e?.response?.data?.detail || 'Could not load folders.'))
      .finally(() => setLoading(false));
  }, []);

  const byId = useMemo(() => {
    const m = new Map<string, VideoFolder>();
    folders.forEach((f) => m.set(f.id, f));
    return m;
  }, [folders]);
  const pathOf = useCallback((f: VideoFolder) => {
    const parts: string[] = []; let cur: VideoFolder | undefined = f; let guard = 0;
    while (cur && guard++ < 64) { parts.unshift(cur.name); cur = cur.parent_id ? byId.get(cur.parent_id) : undefined; }
    return parts.join(' / ');
  }, [byId]);

  const dests = useMemo<{ id: string | null; label: string }[]>(() => {
    const list = folders.map((f) => ({ id: f.id as string | null, label: pathOf(f) }));
    list.sort((a, b) => a.label.localeCompare(b.label));
    return [{ id: null, label: 'Library (root)' }, ...list];
  }, [folders, pathOf]);
  const ql = q.trim().toLowerCase();
  const shown = dests.filter((d) => !ql || d.label.toLowerCase().includes(ql));
  const currentId = video.folder_id ?? null;

  const moveTo = async (folderId: string | null) => {
    if (busy || folderId === currentId) return;
    setBusy(true); setErr('');
    try {
      await api.patch(`/api/videos/${video.id}`, { folder_id: folderId });
      onDone();
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Could not move the video.'); setBusy(false); }
  };

  const text = isDark ? 'text-white' : 'text-gray-900';
  const sub = isDark ? 'text-slate-400' : 'text-gray-500';

  return (
    <>
      <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.96, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={cx('flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl shadow-lq-glass',
            isDark ? 'border border-slate-700/60 bg-slate-900' : 'border border-gray-200 bg-white')}>

          <div className={cx('flex flex-none items-start justify-between gap-3 border-b px-6 py-5', isDark ? 'border-slate-800' : 'border-gray-100')}>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Move video</div>
              <h2 className={cx('mt-1 flex items-center gap-2 text-lg font-bold', text)}>
                <VideoCameraIcon className="h-5 w-5 flex-none text-cyan-500" /><span className="truncate">{video.title}</span>
              </h2>
              <p className={cx('mt-1 text-sm', sub)}>Pick a destination folder, or send it back to the library root.</p>
            </div>
            <button onClick={onClose} className={cx('flex-none rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-500/10', isDark ? 'hover:text-white' : 'hover:text-gray-700')}><XMarkIcon className="h-5 w-5" /></button>
          </div>

          <div className={cx('flex flex-none items-center gap-2 border-b px-6 py-3', isDark ? 'border-slate-800' : 'border-gray-100')}>
            <MagnifyingGlassIcon className={cx('h-4 w-4', sub)} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search folders…"
              className={cx('w-full bg-transparent text-sm outline-none placeholder:text-slate-400', text)} />
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {loading ? (
              <div className="space-y-1.5 px-3">
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className={cx('h-10 animate-pulse rounded-lg', isDark ? 'bg-slate-800/60' : 'bg-gray-100')} />)}
              </div>
            ) : shown.length === 0 ? (
              <p className={cx('px-3 py-8 text-center text-sm', sub)}>No folders match.</p>
            ) : (
              <div className="space-y-0.5">
                {shown.map((d) => {
                  const isCurrent = d.id === currentId;
                  return (
                    <button key={d.id ?? '__root__'} onClick={() => moveTo(d.id)} disabled={busy || isCurrent}
                      className={cx('flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition',
                        isCurrent ? 'cursor-default opacity-60' : (isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-50'))}>
                      {d.id === null ? <HomeIcon className="h-4 w-4 flex-none text-slate-400" /> : <FolderIcon className="h-4 w-4 flex-none text-cyan-500" />}
                      <span className={cx('min-w-0 flex-1 truncate', text)}>{d.label}</span>
                      {isCurrent && <span className={cx('flex-none text-[11px] font-medium', sub)}>Current</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {err && <div className="mx-3 mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</div>}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

// ── Student view — flat, read-only "shared with me" grid, grouped by book ──
// No folder tree: students get the lectures shared with them organized by the
// curriculum axis (book → chapter), the way they actually think about content.
const StudentLibrary: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    api.get<Video[]>('/api/videos')
      .then((r) => setVideos(r.data))
      .catch((e: any) => setError(e?.response?.data?.detail || 'Failed to load your lectures.'))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => videos.filter((v) =>
    !q || v.title.toLowerCase().includes(q) || (v.book_title || '').toLowerCase().includes(q)
    || (v.concept_tags || []).some((c) => c.toLowerCase().includes(q))), [videos, q]);

  // Group by book; videos sorted by chapter within. Untagged lectures sink to the end.
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; title: string; videos: Video[] }>();
    for (const v of shown) {
      const key = v.book_id || '__other__';
      if (!map.has(key)) map.set(key, { key, title: v.book_title || 'Other lectures', videos: [] });
      map.get(key)!.videos.push(v);
    }
    const arr = Array.from(map.values());
    arr.forEach((g) => g.videos.sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0) || a.title.localeCompare(b.title)));
    // Untagged bucket sinks to the end; everything else alphabetical by book title.
    arr.sort((a, b) => (a.key === '__other__' ? 1 : 0) - (b.key === '__other__' ? 1 : 0) || a.title.localeCompare(b.title));
    return arr;
  }, [shown]);

  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const sub = isDarkMode ? 'text-slate-400' : 'text-gray-500';
  const inputWrap = isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-300 bg-white';
  const empty = !loading && shown.length === 0;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-5 py-7 md:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400">Library</div>
            <h1 className={cx('mt-1 text-2xl font-bold sm:text-3xl', text)}>Content Library</h1>
            <p className={cx('mt-1 text-sm', sub)}>{videos.length} lecture{videos.length === 1 ? '' : 's'} shared with you</p>
          </div>
          {videos.length > 0 && (
            <div className={cx('flex items-center gap-2 rounded-xl border px-3 py-2', inputWrap)}>
              <MagnifyingGlassIcon className={cx('h-4 w-4', sub)} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search lectures…"
                className={cx('w-40 bg-transparent text-sm outline-none placeholder:text-slate-400 sm:w-56', text)} />
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 flex items-center justify-between rounded-xl bg-red-500/10 px-4 py-2.5 text-sm text-red-500">
            <span>{error}</span><button onClick={() => setError('')}><XMarkIcon className="h-4 w-4" /></button>
          </div>
        )}

        <div className="mt-7">
          {loading ? (
            <Skeleton isDark={isDarkMode} />
          ) : empty ? (
            <StudentEmptyState isDark={isDarkMode} filtered={!!q} />
          ) : (
            <motion.div variants={gridV} initial="hidden" animate="show" className="space-y-9">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="mb-3 flex items-center gap-2">
                    <BookOpenIcon className="h-4 w-4 flex-none text-cyan-500" />
                    <span className={cx('truncate text-sm font-semibold', text)}>{g.title}</span>
                    <span className={cx('flex-none text-xs', sub)}>· {g.videos.length}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {g.videos.map((v) => (
                      <VideoCard key={v.id} video={v} isDark={isDarkMode} list={false} readOnly
                        onOpen={() => navigate(`/watch/${v.id}`)} />
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const StudentEmptyState: React.FC<{ isDark: boolean; filtered: boolean }> = ({ isDark, filtered }) => (
  <div className={cx('flex flex-col items-center justify-center rounded-2xl border border-dashed py-20 text-center', isDark ? 'border-slate-700' : 'border-gray-200')}>
    <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-glow-cyan" style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
      <VideoCameraIcon className="h-8 w-8" />
    </span>
    <h3 className={cx('text-lg font-bold', isDark ? 'text-white' : 'text-gray-900')}>{filtered ? 'No matches' : 'No lectures yet'}</h3>
    <p className={cx('mt-1 max-w-sm text-sm', isDark ? 'text-slate-400' : 'text-gray-500')}>
      {filtered ? 'Try a different search.' : 'When your teacher shares a lecture with your class, it shows up here.'}
    </p>
  </div>
);

export default VideoLibrary;
